import { Loader2 } from 'lucide-react';
import React, { useState, useCallback, useRef, useMemo } from 'react';

import { debounce } from '@/utils/debounce';

import type { ButtonProps } from './button';
import { Button } from './button';

export interface SaveButtonProps extends Omit<ButtonProps, 'onClick'> {
  /**
   * Text to display when button is idle
   */
  children: React.ReactNode;

  /**
   * Text to display when saving is in progress
   */
  loadingText?: string;

  /**
   * Function to call when the button is clicked
   */
  onClick: () => void | Promise<void>;

  /**
   * Time in milliseconds to debounce the click handler
   * @default 300
   */
  debounceTime?: number;

  /**
   * Whether the button is in a loading state
   * If provided, this will override the internal loading state
   */
  isLoading?: boolean;
}

/**
 * SaveButton component that prevents duplicate submissions
 *
 * This button implements multiple strategies to prevent duplicate form submissions:
 * 1. Tracks loading state and disables the button during submission
 * 2. Debounces the click handler to prevent rapid clicks
 * 3. Uses a ref to track if a submission is already in progress
 */
export function SaveButton({
  children,
  loadingText = 'جاري الحفظ...',
  onClick,
  disabled,
  debounceTime = 300,
  isLoading: externalLoading,
  ...props
}: SaveButtonProps) {
  // Local loading state (used if externalLoading is not provided)
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  // Use external loading state if provided, otherwise use local state
  const isLoading =
    externalLoading !== undefined ? externalLoading : isLocalLoading;

  // Use a ref to prevent concurrent executions
  const isSavingRef = useRef(false);

  // Handle click with debounce and loading state
  const debouncedHandler = useCallback(async () => {
    // Prevent duplicate executions
    if (isSavingRef.current) {
      console.warn('Save already in progress, ignoring duplicate click');
      return;
    }

    try {
      // Set loading state
      isSavingRef.current = true;
      setIsLocalLoading(true);

      // Execute the click handler
      await onClick();
    } finally {
      // Ensure loading state is reset even if the handler throws
      setTimeout(() => {
        isSavingRef.current = false;
        setIsLocalLoading(false);
      }, 300);
    }
  }, [onClick]);

  const handleClick = useMemo(
    () => debounce(debouncedHandler, debounceTime),
    [debouncedHandler, debounceTime],
  );

  return (
    <Button {...props} disabled={disabled || isLoading} onClick={handleClick}>
      {isLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {loadingText}
        </>
      ) : (
        children
      )}
    </Button>
  );
}
